// Sugar Alarm — ticking clock for the header age display.
//
// Returns the current epoch ms, updated every `intervalMs`. Defaults to
// 30s — the age display only changes per-minute, but 30s feels snappier
// without burning meaningful battery.
//
// ⚠ CYCLE 435 — THIS CLOCK IS FOR LABELS, NEVER FOR A FRESHNESS DECISION.
// This hook had one commit in its life (2026-05-08) and no resume handling:
// a suspended iOS webview runs no timers, so the value here stops with the
// app and the first render after a thaw still sees the pre-freeze time. On
// 2026-09-14 20:27Z that is how a 13:47 reading was judged fresh at 20:27
// and painted as current for ~1.3 s. The FIX is not here: every freshness
// decision now goes through `lib/currentGlucose.ts`, which samples
// `Date.now()` at the call. The resume listener below is an OPTIMISATION
// on top of that — it makes the age labels catch up on the first frame
// instead of at the next tick — and the acceptance test for the thaw
// passes with this listener absent, deliberately.

import { useEffect, useState } from "react";

export function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    // Cycle 435: catch up on resume, so labels do not wait out a whole
    // interval after a thaw. Best-effort; never a freshness input.
    const onResume = () => {
      if (typeof document === "undefined" || document.visibilityState !== "hidden") {
        setNow(Date.now());
      }
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onResume);
    }
    window.addEventListener("pageshow", onResume);
    return () => {
      window.clearInterval(id);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onResume);
      }
      window.removeEventListener("pageshow", onResume);
    };
  }, [intervalMs]);
  return now;
}
